export const DEFAULT_DELEGATION_PERMISSIONS = [
  'invoice.buy:view',
  'invoice.buy:manage',
  'invoice.sell:view',
  'invoice.sell:manage',
  'invoice.virtual.sell:view',
  'invoice.virtual.buy:view',
  'contract.brokerage:view',
] as const

export const CONTRACT_REACTION_PERMISSION = 'contract.brokerage:manage'
export const MEMORY_UNIQUE_ID_PERMISSION = 'memory-unique-id:manage'

export const DELEGATION_PERMISSIONS = [
  ...DEFAULT_DELEGATION_PERMISSIONS,
  CONTRACT_REACTION_PERMISSION,
  MEMORY_UNIQUE_ID_PERMISSION,
] as const

export const PERMISSION_LABELS: Record<string, string> = {
  'invoice.buy:view': 'مشاهده صورتحساب‌های خرید',
  'invoice.buy:manage': 'واکنش به صورتحساب‌های خرید',
  'invoice.sell:view': 'مشاهده صورتحساب‌های فروش',
  'invoice.sell:manage': 'واکنش به صورتحساب‌های فروش',
  'invoice.virtual.sell:view': 'مشاهده صورتحساب‌های مجازی فروش',
  'invoice.virtual.buy:view': 'مشاهده صورتحساب‌های مجازی خرید',
  'contract.brokerage:view': 'مشاهده قراردادهای شخص ثالث',
  'contract.brokerage:manage': 'مدیریت قراردادهای شخص ثالث',
  'memory-unique-id:manage': 'مدیریت شناسه یکتای حافظه مالیاتی',
}

// One plain sentence per permission, shown under its label in the delegation panel so the
// taxpayer knows exactly what each access lets the trusted company do on their workspace.
export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  'invoice.buy:view': 'دیدن فهرست و جزئیات صورتحساب‌های خرید در کارپوشه.',
  'invoice.buy:manage': 'تأیید یا رد صورتحساب‌های خرید در کارپوشه.',
  'invoice.sell:view': 'دیدن فهرست و جزئیات صورتحساب‌های فروش در کارپوشه.',
  'invoice.sell:manage': 'تأیید یا رد صورتحساب‌های فروش (مجازی) در کارپوشه.',
  'invoice.virtual.sell:view': 'دیدن صورتحساب‌های مجازی فروش عرضه‌کننده واسط/حق‌العمل‌کار.',
  'invoice.virtual.buy:view': 'دیدن صورتحساب‌های مجازی خرید عرضه‌کننده واسط/حق‌العمل‌کار.',
  'contract.brokerage:view': 'دیدن قراردادهای معامله از طریق شخص ثالث.',
  'contract.brokerage:manage': 'تأیید یا رد قراردادهای معامله از طریق شخص ثالث.',
  'memory-unique-id:manage': 'دریافت و غیرفعال‌سازی شناسه یکتای حافظه مالیاتی از طریق شرکت معتمد.',
}

export const TAX_FILE_STATUS_LABELS: Record<string, string> = {
  NOT_ALLOCATED: 'تخصیص نیافته',
  ACTIVE: 'فعال',
  TEMPORARY_UNAUTHORIZE: 'غیرمجاز موقت',
  PERMANENT_UNAUTHORIZE: 'غیرمجاز دائم',
}

export const TAXPAYER_GENERAL_STATUS_LABELS: Record<string, string> = {
  ENABLED: 'فعال',
  DISABLED: 'غیرفعال',
}

export const LOG_OPERATION_LABELS: Record<string, string> = {
  RequestOtp: 'درخواست دسترسی و ارسال رمز یک‌بارمصرف',
  VerifyOtp: 'تأیید رمز یک‌بارمصرف و فعال‌سازی دسترسی',
  ApproveInvoices: 'تأیید صورتحساب',
  RejectInvoices: 'رد صورتحساب',
  BulkInvoiceItem: 'عملیات گروهی صورتحساب',
  ApproveContract: 'تأیید قرارداد شخص ثالث',
  RejectContract: 'رد قرارداد شخص ثالث',
  BulkContractItem: 'عملیات گروهی قرارداد',
  ApproveContractDisabled: 'تأیید قرارداد (سرویس غیرفعال)',
  RejectContractDisabled: 'رد قرارداد (سرویس غیرفعال)',
  ApproveContractPermissionDenied: 'تأیید قرارداد (بدون مجوز)',
  RejectContractPermissionDenied: 'رد قرارداد (بدون مجوز)',
  TaxFileStatus: 'استعلام وضعیت کارپوشه',
  AddMemoryUniqueId: 'دریافت شناسه یکتای حافظه مالیاتی',
  DisableMemoryUniqueId: 'غیرفعال‌سازی شناسه یکتای حافظه مالیاتی',
  SearchInvoices: 'جستجوی صورتحساب‌ها',
  SearchContracts: 'جستجوی قراردادها',
  ValidateContractReaction: 'بررسی وضعیت قرارداد پیش از واکنش',
  BulkInvoices: 'واکنش گروهی به صورتحساب‌ها',
  BulkContracts: 'واکنش گروهی به قراردادها',
  ExportInvoices: 'خروجی اکسل صورتحساب‌ها',
  ExportContracts: 'خروجی اکسل قراردادها',
  PreviewInvoicesExcel: 'بررسی فایل اکسل صورتحساب‌ها',
  PreviewContractsExcel: 'بررسی فایل اکسل قراردادها',
  ExecuteInvoicesExcel: 'اجرای فایل اکسل صورتحساب‌ها',
  ExecuteContractsExcel: 'اجرای فایل اکسل قراردادها',
}

export const LOG_RESULT_LABELS: Record<string, string> = {
  SUCCESS: 'موفق',
  FAILED: 'ناموفق',
  AMBIGUOUS: 'نامشخص',
  IN_PROGRESS: 'در حال انجام',
  SKIPPED: 'بدون اقدام',
}

/** Where an operation stopped (backend TaxWorkspaceAuditStages). */
export const LOG_STAGE_LABELS: Record<string, string> = {
  LOCAL: 'ارسال نشد',
  NONCE: 'خطا در دریافت nonce',
  SIGNING: 'خطا در امضای گواهی',
  TRANSPORT: 'پاسخی دریافت نشد',
  ORGANIZATION: 'پاسخ سازمان',
  RESPONSE: 'خطا در پردازش پاسخ',
}

export const LOG_STAGE_DESCRIPTIONS: Record<string, string> = {
  LOCAL:
    'درخواست پیش از ارسال به سازمان متوقف شد؛ به دلیل اعتبارسنجی ورودی، دسترسی کاربر، وضعیت تفویض یا قواعد برنامه.',
  NONCE: 'دریافت nonce از سامانه مؤدیان، که پیش‌نیاز هر درخواست است، ناموفق بود.',
  SIGNING: 'امضای درخواست با گواهی شرکت معتمد انجام نشد؛ موضوع باید توسط پشتیبانی بررسی شود.',
  TRANSPORT: 'درخواست ارسال شد اما پاسخی از سامانه مؤدیان نرسید (قطع ارتباط یا پایان زمان انتظار).',
  ORGANIZATION: 'سامانه مؤدیان به درخواست پاسخ داد؛ نتیجه و پیام همان پاسخ سازمان است.',
  RESPONSE: 'پاسخ سامانه مؤدیان رسید اما محتوای آن قابل پردازش نبود.',
}

export const LOG_SOURCE_LABELS: Record<string, string> = {
  UI: 'پنل',
  EXCEL: 'فایل اکسل',
  API_V2: 'API نسخه ۲',
  // «اعلان‌های کارپوشه» و «تأیید خودکار صورتحساب‌های خرید»، بدون کاربر واردشده.
  SYSTEM: 'سرویس خودکار',
}

export const PROFILE_STATUS_LABELS: Record<string, string> = {
  NotConnected: 'متصل نشده',
  PendingOtp: 'در انتظار تأیید رمز',
  Active: 'فعال',
  Expired: 'منقضی‌شده',
  Revoked: 'لغوشده',
  Invalid: 'نامعتبر',
  Error: 'دارای خطا',
}

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  REJECTED: 'ردشده',
  APPROVED: 'تأییدشده',
  SYSTEMIC_APPROVED: 'تأیید سیستمی',
  IMPOSSIBLE_REACTION: 'غیرقابل واکنش',
  AWAITING_REACTION: 'در انتظار واکنش',
  NO_NEED_REACTION: 'بدون نیاز به واکنش',
  CANCELED: 'ابطال‌شده',
  SYSTEMIC_REJECTED: 'رد سیستمی',
}

export const INVOICE_TYPE_LABELS: Record<string, string> = {
  FIRST: 'نوع اول',
  SECOND: 'نوع دوم',
}

export const INVOICE_PATTERN_LABELS: Record<string, string> = {
  SALE: 'فروش',
  CURRENCY_SALE: 'فروش ارز',
  GOLD: 'طلا و جواهر',
  CONTRACT: 'پیمانکاری',
  BILL: 'قبوض خدماتی',
  PLANE_TICKET: 'بلیت هواپیما',
  EXPORT: 'صادرات',
  LADING: 'بارنامه',
  REFINEMENT_AND_DISTRIBUTION: 'پالایش و توزیع فرآورده‌های نفتی',
  MERCANTILE_EXCHANGE_SALE: 'فروش در بورس کالا',
  INSURANCE_SERVICES: 'خدمات بیمه',
  CHAIN: 'فروش زنجیره‌ای',
  MERCANTILE_EXCHANGE_BUY: 'خرید از بورس کالا',
  IMPORT: 'واردات',
}

export const INVOICE_SUBJECT_LABELS: Record<string, string> = {
  ORIGINAL: 'اصلی',
  CORRECTIVE: 'اصلاحی',
  CANCELLATION: 'ابطالی',
  RETURN_FROM_SALE: 'برگشت از فروش',
}

export const TAXPAYER_ROLE_LABELS: Record<string, string> = {
  NORMAL: 'عادی',
  BROKER_CLIENT: 'آمر',
  BROKER: 'حق‌العمل‌کار',
}

export const ARTICLE6_STATUS_LABELS: Record<string, string> = {
  EXCEEDED: 'عبور از حد مجاز ماده ۶',
  NOT_EXCEEDED: 'در محدوده ماده ۶',
}

/** CallStatus is «وضعیت مشمولیت» (TSP-API enumeration-types, tax-file-status), not a notification. */
export const CALL_STATUS_LABELS: Record<string, string> = {
  CALLED: 'مشمول',
  NOT_CALLED: 'غیرمشمول',
}

export const CONTRACT_STATUS_LABELS: Record<string, string> = {
  FINAL_REGISTRATION: 'ثبت نهایی',
  AWAITING_REACTION: 'در انتظار واکنش',
  CANCELLED: 'ابطال‌شده',
  REJECTED: 'ردشده',
}

export const CONTRACT_SUBJECT_LABELS: Record<string, string> = {
  MAIN: 'اصلی',
  CORRECTIVE: 'اصلاحی',
  CANCELLATION: 'ابطالی',
}

export const CONTRACT_TYPE_LABELS: Record<string, string> = {
  BROKERAGE_BUY_CONTRACT: 'قرارداد خرید حق‌العمل‌کاری',
  BROKERAGE_SELL_CONTRACT: 'قرارداد فروش حق‌العمل‌کاری',
  VICARIOUS_SELL_CONTRACT: 'قرارداد فروش نیابتی',
  SHARING_SELL_CONTRACT: 'قرارداد فروش از طریق به‌هم‌رسانی',
}

export const CONTRACT_REGISTRAR_LABELS: Record<string, string> = {
  BROKER: 'حق‌العمل‌کار',
  BROKER_CLIENT: 'آمر',
}

export const REJECTION_REASON_LABELS: Record<string, string> = {
  NOT_INFORMED: 'عدم اطلاع از صورتحساب',
  BUYER: 'اطلاعات خریدار',
  SELLER: 'اطلاعات فروشنده',
  PRODUCT: 'کالا یا خدمت',
  AMOUNT: 'مبلغ صورتحساب',
  PAYMENT: 'اطلاعات پرداخت',
  OTHER: 'سایر موارد',
}

// Token errors only: ACCESS_DENIED is one permission missing from a delegation that still works
// (backend 712, user decision 2026-10-21).
export const REAUTHENTICATION_ERROR_CODES = new Set([
  'tax_workspace_reauthorization_required',
  'TSP_PAT_EXPIRED',
  'TSP_PAT_NOT_FOUND',
  'TSP_PAT_IS_INVALID',
  'TSP_PAT_REVOKED',
  'DELEGATION_TOKEN_REQUIRED',
  'EXPIRED_PAT_TSP',
  'REVOKED_PAT_TSP',
  'REQUIRED_TOKEN_DELEGATION',
])

export const PERMISSION_MISSING_ERROR_CODES = new Set([
  'tax_workspace_permission_missing',
  'ACCESS_DENIED',
  'AccessDenied',
  'DENIED_ACCESS',
])

/** The error's message, naming the permission the page needs when the delegation lacks it. */
export const describeWorkspaceError = (
  error: {message: string; missingPermission?: boolean},
  permission?: string
) =>
  error.missingPermission && permission
    ? `${error.message} مجوز لازم: «${PERMISSION_LABELS[permission] || permission}».`
    : error.message

export const labelFor = (map: Record<string, string>, value: unknown) => {
  if (value === null || value === undefined || value === '') return '—'
  const wireValue = String(value)
  return map[wireValue] || wireValue
}
